import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Clock,
  Info,
  RefreshCw,
  Wifi,
  WifiOff
} from 'lucide-react';
import React from 'react';

export type ErrorType = 'network' | 'auth' | 'server' | 'timeout' | 'not_found' | 'unknown';

export interface ErrorState {
  type: ErrorType;
  message: string;
  details?: string;
  canRetry: boolean;
}

export interface LoadingStateProps {
  message?: string;
  showNetworkStatus?: boolean;
  isOnline?: boolean;
}

export interface ErrorStateProps {
  error: ErrorState;
  onRetry?: () => void;
  retryCount?: number;
  maxRetries?: number;
}

export interface SuccessStateProps {
  message?: string;
  showLastUpdated?: boolean;
  lastUpdated?: Date;
  showNetworkStatus?: boolean;
  isOnline?: boolean;
}

export interface InfoBannerProps {
  message: string;
  type?: 'info' | 'success' | 'warning' | 'error';
  showIcon?: boolean;
}

// Utility functions
export const getErrorIcon = (errorType: ErrorType, size: 'sm' | 'md' | 'lg' = 'md') => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  switch (errorType) {
    case 'network':
      return <WifiOff className={`${sizeClasses[size]} text-red-500`} />;
    case 'auth':
      return <AlertTriangle className={`${sizeClasses[size]} text-yellow-500`} />;
    case 'server':
      return <AlertCircle className={`${sizeClasses[size]} text-orange-500`} />;
    case 'timeout':
      return <Clock className={`${sizeClasses[size]} text-blue-500`} />;
    case 'not_found':
      return <AlertCircle className={`${sizeClasses[size]} text-gray-500`} />;
    default:
      return <AlertCircle className={`${sizeClasses[size]} text-red-500`} />;
  }
};

export const getErrorColor = (errorType: ErrorType) => {
  switch (errorType) {
    case 'network':
      return 'border-red-200 bg-red-50';
    case 'auth':
      return 'border-yellow-200 bg-yellow-50';
    case 'server':
      return 'border-orange-200 bg-orange-50';
    case 'timeout':
      return 'border-blue-200 bg-blue-50';
    case 'not_found':
      return 'border-gray-200 bg-gray-50';
    default:
      return 'border-red-200 bg-red-50';
  }
};

export const getInfoColor = (type: 'info' | 'success' | 'warning' | 'error') => {
  switch (type) {
    case 'info':
      return 'border-blue-200 bg-blue-50 text-blue-800';
    case 'success':
      return 'border-green-200 bg-green-50 text-green-800';
    case 'warning':
      return 'border-yellow-200 bg-yellow-50 text-yellow-800';
    case 'error':
      return 'border-red-200 bg-red-50 text-red-800';
    default:
      return 'border-blue-200 bg-blue-50 text-blue-800';
  }
};

export const getInfoIcon = (type: 'info' | 'success' | 'warning' | 'error', size: 'sm' | 'md' = 'md') => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5'
  };

  switch (type) {
    case 'info':
      return <Info className={`${sizeClasses[size]} text-blue-500`} />;
    case 'success':
      return <CheckCircle className={`${sizeClasses[size]} text-green-500`} />;
    case 'warning':
      return <AlertTriangle className={`${sizeClasses[size]} text-yellow-500`} />;
    case 'error':
      return <AlertCircle className={`${sizeClasses[size]} text-red-500`} />;
    default:
      return <Info className={`${sizeClasses[size]} text-blue-500`} />;
  }
};

// Component exports
export const LoadingState: React.FC<LoadingStateProps> = ({
  message = "Loading...",
  showNetworkStatus = false,
  isOnline = navigator.onLine
}) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center space-x-2">
      <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
      <span className="text-sm text-gray-600">{message}</span>
    </div>
    {showNetworkStatus && (
      <div className="flex items-center space-x-2">
        <Wifi className={`h-4 w-4 ${isOnline ? 'text-green-500' : 'text-red-500'}`} />
        <span className="text-xs text-gray-500">
          {isOnline ? 'Online' : 'Offline'}
        </span>
      </div>
    )}
  </div>
);

export const ErrorState: React.FC<ErrorStateProps> = ({
  error,
  onRetry,
  retryCount = 0,
  maxRetries = 3
}) => (
  <div className={`rounded-lg border p-4 ${getErrorColor(error.type)}`}>
    <div className="flex items-start space-x-3">
      {getErrorIcon(error.type)}
      <div className="flex-1">
        <h3 className="text-sm font-medium text-gray-900">{error.message}</h3>
        {error.details && (
          <p className="text-sm text-gray-700 mt-1">{error.details}</p>
        )}
        {retryCount > 0 && (
          <p className="text-xs text-gray-600 mt-2">
            Attempt {retryCount + 1} of {maxRetries}
          </p>
        )}
      </div>
      {error.canRetry && onRetry && (
        <button
          onClick={onRetry}
          disabled={retryCount >= maxRetries}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          {retryCount >= maxRetries ? 'Max retries reached' : 'Try Again'}
        </button>
      )}
    </div>
  </div>
);

export const SuccessState: React.FC<SuccessStateProps> = ({
  message = "Loaded successfully",
  showLastUpdated = false,
  lastUpdated,
  showNetworkStatus = false,
  isOnline = navigator.onLine
}) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center space-x-2">
      <CheckCircle className="h-4 w-4 text-green-500" />
      <span className="text-sm text-gray-600">{message}</span>
    </div>
    {(showNetworkStatus || showLastUpdated) && (
      <div className="flex items-center space-x-3">
        {showNetworkStatus && (
          <div className="flex items-center space-x-2">
            <Wifi className={`h-4 w-4 ${isOnline ? 'text-green-500' : 'text-red-500'}`} />
            <span className="text-xs text-gray-500">
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
        )}
        {showLastUpdated && lastUpdated && (
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <Clock className="h-3 w-3" />
            <span>Updated {lastUpdated.toLocaleTimeString()}</span>
          </div>
        )}
      </div>
    )}
  </div>
);

export const InfoBanner: React.FC<InfoBannerProps> = ({
  message,
  type = 'info',
  showIcon = true
}) => (
  <div className={`rounded-lg border p-3 ${getInfoColor(type)}`}>
    <div className="flex items-center space-x-2">
      {showIcon && getInfoIcon(type)}
      <span className="text-sm">{message}</span>
    </div>
  </div>
);

export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}> = ({ icon, title, description, action }) => (
  <div className="px-6 py-8 text-center">
    <div className="text-gray-500 flex flex-col items-center justify-center">
      {icon || <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />}
      <p className="mb-2">{title}</p>
      {description && (
        <p className="text-sm text-gray-400">{description}</p>
      )}
      {action && (
        <div className="mt-4">
          {action}
        </div>
      )}
    </div>
  </div>
);

export const SkeletonLoader: React.FC<{
  rows?: number;
  showAvatar?: boolean;
  className?: string;
}> = ({ rows = 3, showAvatar = true, className = "" }) => (
  <div className={`space-y-4 ${className}`}>
    {[...Array(rows)].map((_, index) => (
      <div key={index} className="flex items-start space-x-3">
        {showAvatar && (
          <div className="p-2 rounded-full bg-gray-100 animate-pulse">
            <div className="h-4 w-4 bg-gray-300 rounded"></div>
          </div>
        )}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
          <div className="animate-pulse">
            <div className="h-3 bg-gray-100 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Export all components and utilities
const UIStates = {
  LoadingState,
  ErrorState,
  SuccessState,
  InfoBanner,
  EmptyState,
  SkeletonLoader,
  getErrorIcon,
  getErrorColor,
  getInfoColor,
  getInfoIcon
};

export default UIStates;


